import { Stage } from "../../lusca-search-api/generated-src";

// A prepared redrive outlives the tab for this long. The MCM approval gap can
// span days, but a weeks-old upload is stale; the S3 object itself is kept.
export const PREPARED_REDRIVE_MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000;

// Per-stage localStorage key, so a prepared upload and its MCM survive a tab
// close during the approval gap.
export const preparedRedriveStorageKey = (stage: Stage) =>
  `lusca-psns-redrive-upload-${stage}`;

export interface PreparedPsnsRedrive {
  s3Uri: string;
  etag: string;
  count: number;
  savedAt: number;
  dryRunExecutionArn?: string;
  mcmId?: string;
  mcmUrl?: string;
  warning?: string;
}

const optionalString = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value : undefined;

export const parsePreparedRedrive = (
  raw: string,
  now: number
): PreparedPsnsRedrive | null => {
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object") {
    return null;
  }
  const values = parsed as Record<string, unknown>;
  if (
    typeof values.s3Uri !== "string" ||
    typeof values.etag !== "string" ||
    typeof values.count !== "number" ||
    typeof values.savedAt !== "number"
  ) {
    return null;
  }
  if (now - values.savedAt > PREPARED_REDRIVE_MAX_AGE_MS) {
    return null;
  }
  return {
    s3Uri: values.s3Uri,
    etag: values.etag,
    count: values.count,
    savedAt: values.savedAt,
    dryRunExecutionArn: optionalString(values.dryRunExecutionArn),
    mcmId: optionalString(values.mcmId),
    mcmUrl: optionalString(values.mcmUrl),
    warning: optionalString(values.warning),
  };
};

export const clearPreparedRedrive = (stage: Stage) => {
  try {
    localStorage.removeItem(preparedRedriveStorageKey(stage));
  } catch {
    // Ignore storage errors.
  }
};

// Missing, unreadable and expired entries all read as nothing prepared; an
// unusable entry is removed so it is not re-parsed on every load.
export const loadPreparedRedrive = (
  stage: Stage,
  now = Date.now()
): PreparedPsnsRedrive | null => {
  let raw: string | null;
  try {
    raw = localStorage.getItem(preparedRedriveStorageKey(stage));
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  let prepared: PreparedPsnsRedrive | null;
  try {
    prepared = parsePreparedRedrive(raw, now);
  } catch {
    prepared = null;
  }
  if (!prepared) {
    clearPreparedRedrive(stage);
  }
  return prepared;
};

export const savePreparedRedrive = (
  stage: Stage,
  prepared: PreparedPsnsRedrive
) => {
  try {
    localStorage.setItem(
      preparedRedriveStorageKey(stage),
      JSON.stringify(prepared)
    );
  } catch {
    // Non-fatal: persistence is a convenience.
  }
};
